import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { SliderWrapper } from "../index";
import { alertNotification } from "../../../components/TopNotification/alertNotification";
import { Spin } from "antd";
import {
  passwordlessConfirmApi,
  passwordlessStartApi,
} from "../../../API/API";
import styles from "./VerifyEmail.module.css";
import { setCookie } from "../../../shared/helpers";

const PASSWORDLESS_CODE_LENGTH = 4;
const PASSWORDLESS_RESEND_LIMIT = 2;
const PASSWORDLESS_RESEND_COOLDOWN = 60;


export const VerifyEmail = () => {
  const location = useLocation();

  const email = location.state?.email || "";
  const fromSignup = Boolean(location.state?.fromSignup);
  const fromLogin = Boolean(location.state?.fromLogin);
  const codeLength = PASSWORDLESS_CODE_LENGTH;
  const [challengeSession, setChallengeSession] = useState(
    location.state?.challengeSession || ""
  );

  useEffect(() => {
    if (!fromSignup && !fromLogin) {
      localStorage.removeItem('emailVerified');
      window.location.replace("/employer/auth");
      return;
    }

    localStorage.setItem('emailVerified', 'false');
  }, [fromSignup, fromLogin]); // eslint-disable-line react-hooks/exhaustive-deps

  const [code, setCode] = useState(Array(codeLength).fill(""));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [passwordlessResendCount, setPasswordlessResendCount] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputs = useRef([]);
  const isPasswordlessFlow = fromSignup || fromLogin;
  const passwordlessResendsRemaining =
    PASSWORDLESS_RESEND_LIMIT - passwordlessResendCount;
  const isPasswordlessResendDisabled =
    isPasswordlessFlow &&
    (resendCooldown > 0 ||
      resendLoading ||
      passwordlessResendsRemaining <= 0);

  useEffect(() => {
    if (!resendCooldown) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[idx] = val;
    setCode(next);
    setErrorMsg("");
    if (val && idx < codeLength - 1) {
      inputs.current[idx + 1]?.focus();
    }
    if (val && idx === codeLength - 1) {
      const fullCode = next.join("");
      if (fullCode.length === codeLength) handleVerifyCode(fullCode);
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, codeLength);
    if (!pasted) return;
    const next = Array(codeLength).fill("");
    pasted.split("").forEach((ch, i) => { next[i] = ch; });
    setCode(next);
    inputs.current[Math.min(pasted.length, codeLength - 1)]?.focus();
    if (pasted.length === codeLength) handleVerifyCode(pasted);
  };

  const handleVerifyCode = async (fullCode) => {
    setLoading(true);
    setErrorMsg("");
    try {
      if (fromSignup) {
        const response = await passwordlessConfirmApi({
          email,
          code: fullCode,
          session: challengeSession,
        });
        const data = response?.data || {};

        if (data.token || data.idToken) {
          setCookie("token", data.token || data.idToken, 30);
        }

        localStorage.setItem("Email", email);
        localStorage.setItem('emailVerified', 'true');
        window.location.replace("/employer/auth");
        return;
      }

      if (fromLogin) {
        const response = await passwordlessConfirmApi({
          email,
          code: fullCode,
          session: challengeSession,
        });
        const data = response?.data || {};

        if (data.token || data.idToken) {
          setCookie("token", data.token || data.idToken, 30);
        }

        localStorage.setItem("Email", email);
        localStorage.setItem('emailVerified', 'true');
        window.location.replace("/employer/auth");
        return;
      }

    } catch (error) {
      setLoading(false);
      setCode(Array(codeLength).fill(""));
      inputs.current[0]?.focus();
      setErrorMsg("The verification code is invalid. Please enter the correct code or request a new code.");
    }
  };

  const handleResend = async () => {
    if (isPasswordlessResendDisabled) return;

    setResendLoading(true);
    setErrorMsg("");
    try {
      if (fromSignup) {
        const response = await passwordlessStartApi({
          email,
          purpose: "signup",
        });
        setChallengeSession(response?.data?.session || "");
        setPasswordlessResendCount((prev) => prev + 1);
        setResendCooldown(PASSWORDLESS_RESEND_COOLDOWN);
        alertNotification("success", "Verification code resent successfully");
        return;
      }

      if (fromLogin) {
        const response = await passwordlessStartApi({
          email,
          purpose: "login",
        });
        setChallengeSession(response?.data?.session || "");
        setPasswordlessResendCount((prev) => prev + 1);
        setResendCooldown(PASSWORDLESS_RESEND_COOLDOWN);
        alertNotification("success", "Verification code resent successfully");
        return;
      }
    } catch (error) {
      alertNotification("error", "Failed to resend code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <SliderWrapper>
      <div className={styles.card}>
        <h2 className={styles.title}>Verify Email Address</h2>
        <p className={styles.subtitle}>
          Enter the verification code we sent to{" "}
          <span className={styles.email}>{email}</span>
        </p>

        <div className={styles.otpRow} onPaste={handlePaste}>
          {code.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => (inputs.current[idx] = el)}
              className={`${styles.otpBox} ${errorMsg ? styles.otpBoxError : digit ? styles.otpBoxFilled : ""}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e, idx)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              disabled={loading}
            />
          ))}
        </div>

        {loading && <div className={styles.loadingRow}><Spin size="small" /></div>}

        {errorMsg && <p className={styles.errorMsg}>{errorMsg}</p>}

        <p className={styles.resendRow}>
          Didn&apos;t receive the code?{" "}
          {resendLoading ? (
            <Spin size="small" />
          ) : (
            <button
              type="button"
              className={`${styles.resendLink} ${
                isPasswordlessResendDisabled ? styles.resendLinkDisabled : ""
              }`}
              onClick={handleResend}
              disabled={isPasswordlessResendDisabled}
            >
              Resend code
            </button>
          )}
        </p>

        {isPasswordlessFlow && resendCooldown > 0 && (
          <p className={styles.resendHelp}>
            You can resend another code in {resendCooldown}s.
          </p>
        )}

        {isPasswordlessFlow && passwordlessResendsRemaining <= 0 && (
          <p className={styles.resendHelp}>
            You have reached the resend limit. Please try again later.
          </p>
        )}
      </div>
    </SliderWrapper>
  );
};
